import React, { useRef } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  Paper,
  Divider,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DeleteIcon from "@mui/icons-material/Delete";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import FormatUnderlinedIcon from "@mui/icons-material/FormatUnderlined";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import LinkIcon from "@mui/icons-material/Link";
import AddIcon from "@mui/icons-material/Add";
import { SubmitBundleModalProps, EmailAttachment } from "./types";
import { useSubmitBundleModal } from "./useSubmitBundleModal";
import BaseModal from "../../../../../../components/Modal/BaseModal";

export const SubmitBundleModal: React.FC<SubmitBundleModalProps> = (props) => {
  const { open, onClose, generatedDocuments } = props;
  const fileInputRef = useRef<HTMLInputElement>(null);

  const {
    to,
    setTo,
    cc,
    setCc,
    subject,
    setSubject,
    body,
    setBody,
    attachments,
    availableDocuments,
    isSending,
    canSend,
    custodianName,
    addGeneratedDocument,
    removeAttachment,
    handleFileUpload,
    handleSend,
    formatFileSize,
  } = useSubmitBundleModal(props);

  const handleAddFromFinder = () => {
    fileInputRef.current?.click();
  };

  const renderAttachmentCard = (attachment: EmailAttachment) => (
    <Paper
      key={attachment.id}
      variant="outlined"
      sx={{
        p: 1.5,
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        minWidth: 200,
        maxWidth: 280,
        borderRadius: 2,
      }}
    >
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: 1,
          bgcolor: "#FFEBEE",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <PictureAsPdfIcon sx={{ color: "#E53935" }} />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>
          {attachment.name}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {formatFileSize(attachment.size)}
        </Typography>
      </Box>
      <Box sx={{ display: "flex", gap: 0.5 }}>
        <Tooltip title="Preview">
          <IconButton size="small">
            <VisibilityIcon fontSize="small" sx={{ color: "text.secondary" }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Remove">
          <IconButton
            size="small"
            onClick={() => removeAttachment(attachment.id)}
            sx={{ color: "error.main" }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    </Paper>
  );

  const renderAvailableDocumentCard = (doc: EmailAttachment) => {
    const isAttached = attachments.some((a) => a.documentId === doc.documentId);
    return (
      <Paper
        key={doc.id}
        variant="outlined"
        sx={{
          p: 1.5,
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          minWidth: 200,
          maxWidth: 280,
          borderRadius: 2,
          opacity: isAttached ? 0.5 : 1,
          cursor: isAttached ? "default" : "pointer",
          "&:hover": isAttached
            ? {}
            : {
                borderColor: "primary.main",
                bgcolor: "primary.50",
              },
        }}
        onClick={() => !isAttached && addGeneratedDocument(doc)}
      >
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: 1,
            bgcolor: "#FFEBEE",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <PictureAsPdfIcon sx={{ color: "#E53935" }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>
            {doc.name}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {isAttached ? "Already attached" : "Click to attach"}
          </Typography>
        </Box>
        {!isAttached && (
          <AddIcon fontSize="small" color="primary" />
        )}
      </Paper>
    );
  };

  const footer = (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        width: "100%",
      }}
    >
      <Typography variant="caption" color="text.secondary">
        {attachments.length} attachment{attachments.length !== 1 ? "s" : ""} &bull;{" "}
        {body.length} characters
      </Typography>
      <Box sx={{ display: "flex", gap: 1.5 }}>
        <Button variant="outlined" onClick={onClose} disabled={isSending}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSend}
          disabled={!canSend}
          startIcon={isSending ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {isSending ? "Sending..." : "Confirm & Send Email"}
        </Button>
      </Box>
    </Box>
  );

  return (
    <BaseModal
      open={open}
      onClose={onClose}
      title="Review Outgoing Mail"
      subtitle={`Review and send transaction instructions to ${custodianName}`}
      maxWidth="md"
      fullWidth
      customFooter={footer}
      dataTestId="submit-bundle-modal"
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
          {/* To Field */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Typography sx={{ width: 60, color: "text.secondary" }}>To:</Typography>
            <TextField
              value={to}
              onChange={(e) => setTo(e.target.value)}
              placeholder="recipient@example.com"
              fullWidth
              size="small"
              variant="outlined"
            />
          </Box>

          {/* CC Field */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Typography sx={{ width: 60, color: "text.secondary" }}>CC:</Typography>
            <TextField
              value={cc}
              onChange={(e) => setCc(e.target.value)}
              placeholder="cc@example.com, another@example.com"
              fullWidth
              size="small"
              variant="outlined"
            />
          </Box>

          {/* Subject Field */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Typography sx={{ width: 60, color: "text.secondary" }}>Subject:</Typography>
            <TextField
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Email subject"
              fullWidth
              size="small"
              variant="outlined"
            />
          </Box>

          {/* Attachments Section */}
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
              <AttachFileIcon fontSize="small" color="action" />
              <Typography variant="subtitle2">
                Attachments ({attachments.length})
              </Typography>
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={handleAddFromFinder}
                sx={{ ml: "auto" }}
              >
                Add from Finder
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                hidden
                onChange={handleFileUpload}
                accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.txt"
              />
            </Box>

            {/* Attached files */}
            {attachments.length > 0 && (
              <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", mb: 2 }}>
                {attachments.map(renderAttachmentCard)}
              </Box>
            )}

            {/* Available Generated Documents */}
            {generatedDocuments.length > 0 && (
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: "block" }}>
                  Available Generated Documents (click to attach)
                </Typography>
                <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
                  {availableDocuments.map(renderAvailableDocumentCard)}
                </Box>
              </Box>
            )}
          </Box>

          <Divider />

          {/* Rich Text Toolbar */}
          <Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
            <IconButton size="small">
              <FormatBoldIcon fontSize="small" />
            </IconButton>
            <IconButton size="small">
              <FormatItalicIcon fontSize="small" />
            </IconButton>
            <IconButton size="small">
              <FormatUnderlinedIcon fontSize="small" />
            </IconButton>
            <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
            <IconButton size="small">
              <FormatListBulletedIcon fontSize="small" />
            </IconButton>
            <IconButton size="small">
              <LinkIcon fontSize="small" />
            </IconButton>
          </Box>

          {/* Email Body */}
          <TextField
            value={body}
            onChange={(e) => setBody(e.target.value)}
            multiline
            rows={8}
            fullWidth
            variant="outlined"
            placeholder="Write your message here..."
            sx={{
              "& .MuiOutlinedInput-root": {
                fontFamily: "inherit",
              },
            }}
          />
        </Box>
    </BaseModal>
  );
};
